import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlarmClockPlus, LogOut } from 'lucide-react'
import { ApiError, Button, Modal } from '../../core/index.js'
import { useExtendDemoSession } from '../hooks.js'
import { adoptExtendedDemoSession, returnToOpener, type DemoSessionInfo } from '../demoSession.js'

/** How long before the end the session offers more time. */
export const DEMO_EXPIRY_WARNING_MS = 5 * 60 * 1000
const TICK_MS = 1000

export type DemoExpiryStage = 'running' | 'ending' | 'ended'

export function demoExpiryStage(expiresAtMs: number, nowMs: number): DemoExpiryStage {
  const left = expiresAtMs - nowMs
  if (left <= 0) {
    return 'ended'
  }
  return left <= DEMO_EXPIRY_WARNING_MS ? 'ending' : 'running'
}

/** "4:07" for what is left, never below 0:00. */
export function formatCountdown(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/** The clock, re-read every second; a session with no end on record never ticks. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) {
      return
    }
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(timer)
  }, [active])
  return now
}

interface DemoSessionExpiryProps {
  expiresAt: string | null
  openerIsAdmin: boolean
  /** Called with what the banner now shows once the session has its new end. */
  onExtended: (info: DemoSessionInfo | null) => void
}

/**
 * The end of a demo session, said before it happens: in the last five
 * minutes a dialog counts down and offers another hour as the same person
 * (`POST /auth/demo-sessions/extend`), and at the end it says the session is
 * over and offers the way back, rather than the tab falling to a sign-in
 * page on its next call. "Not now" closes the warning; the banner keeps an
 * Extend button until the end.
 */
export function DemoSessionExpiry({ expiresAt, openerIsAdmin, onExtended }: DemoSessionExpiryProps) {
  const endsMs = expiresAt ? Date.parse(expiresAt) : Number.NaN
  const hasEnd = !Number.isNaN(endsMs)
  const now = useNow(hasEnd)
  const queryClient = useQueryClient()
  const extend = useExtendDemoSession()
  const [dismissedFor, setDismissedFor] = useState<string | null>(null)

  if (!hasEnd) {
    return null
  }
  const stage = demoExpiryStage(endsMs, now)
  const runExtend = (): void => {
    extend.mutate(undefined, {
      onSuccess: (response) => {
        onExtended(adoptExtendedDemoSession(response))
        void queryClient.invalidateQueries({ queryKey: ['me'] })
        setDismissedFor(null)
      },
    })
  }
  const extendError = extend.error instanceof ApiError ? extend.error.message : extend.isError ? 'Could not extend the session. Try again.' : null

  if (stage === 'ended') {
    return (
      <Modal
        eyebrow="Demo session"
        title="This demo session has ended"
        subtitle="Demo sessions run for an hour at a time. Return to the account that opened it to open a new one."
        canClose={false}
        onClose={() => undefined}
        footer={
          <div className="flex justify-end">
            <Button icon={LogOut} onClick={() => returnToOpener(openerIsAdmin)}>
              Return
            </Button>
          </div>
        }
      >
        <p className="text-sm text-ink-mid">Nothing more can be done in this tab as this person.</p>
      </Modal>
    )
  }

  return (
    <>
      {stage === 'ending' && (
        <Button icon={AlarmClockPlus} size="sm" busy={extend.isPending} title="Give this demo session another hour" onClick={runExtend}>
          {extend.isPending ? 'Extending' : 'Extend'}
        </Button>
      )}
      {stage === 'ending' && dismissedFor !== expiresAt && (
        <Modal
          eyebrow="Demo session"
          title={`This demo session ends in ${formatCountdown(endsMs - now)}`}
          subtitle="Extend it for another hour as the same person, and carry on where you are."
          canClose={!extend.isPending}
          onClose={() => setDismissedFor(expiresAt)}
          footer={
            <div className="flex flex-wrap items-center justify-end gap-3">
              <button type="button" className="text-sm text-ink-mid hover:underline" onClick={() => setDismissedFor(expiresAt)} disabled={extend.isPending}>
                Not now
              </button>
              <Button icon={LogOut} variant="secondary" onClick={() => returnToOpener(openerIsAdmin)} disabled={extend.isPending}>
                End session
              </Button>
              <Button icon={AlarmClockPlus} busy={extend.isPending} onClick={runExtend}>
                {extend.isPending ? 'Extending' : 'Extend an hour'}
              </Button>
            </div>
          }
        >
          <p className="text-sm text-ink-mid">When it ends, this tab stops working as this person and you return to the account that opened it.</p>
          {extendError && <p className="mt-2 text-sm text-coral">{extendError}</p>}
        </Modal>
      )}
    </>
  )
}
